import { apiClient } from "./api";
import { queryOptions } from "@tanstack/react-query";
import { AxiosError } from "axios";

/* -------------------------------------
 * General Config
 * ------------------------------------- */
export interface GeneralConfig {
  agent_id: string;
  operation_window_start: number;
  operation_window_end: number;
  max_posts_per_day: number;
  auto_replenish_credits: boolean;
  auto_replenish_threshold: number;
  auto_replenish_amount: number;
  timezone: string;
  metadata?: Record<string, any>;
  automations?: any;

  // If you expect more fields, just add them or rely on the next line
  [key: string]: any;
}

export interface GeneralConfigCreate {
  operation_window_start: number;
  operation_window_end: number;
  max_posts_per_day: number;
  auto_replenish_credits?: boolean;
  auto_replenish_threshold?: number;
  auto_replenish_amount?: number;
  timezone?: string;
  metadata?: Record<string, any>;
  automations?: any;

  [key: string]: any;
}

/* -------------------------------------
 * Automation
 * ------------------------------------- */
export interface Automation {
  automation_type: string;
  automation_id: string;
  agent_id: string; // The persona/agent ID
  enabled: boolean;
  product_sku: string;
  name: string;
  description: string;
  created_at: string;
  updated_at: string;
  metadata?: Record<string, any>;

  // Any new or custom fields from backend can appear here:
  [key: string]: any;
}

export interface AutomationCreate {
  automation_type: string;
  product_sku: string;
  name: string;
  description: string;
  metadata?: Record<string, any>;
  enabled: boolean;

  // Any additional fields you want to pass:
  [key: string]: any;
}
export interface GeneralConfigUpdate extends Partial<GeneralConfigCreate> {}

/* ----------------------GeneralConfigUpdate---------------
 * Costs & Limits
 * ------------------------------------- */
export interface AutomationCosts {
  daily_costs: Record<
    string,
    {
      name: string;
      daily_cost: number;
      monthly_cost: number;
      credit_cost_per_action: number;
      [key: string]: any;
    }
  >;
  total_daily_cost: number;
  total_monthly_cost: number;
  operation_hours: number;
  max_posts_per_day: number;
  auto_replenish_details: {
    enabled: boolean;
    threshold: number;
    amount: number;
    estimated_frequency: string | null;
    [key: string]: any;
  };
  [key: string]: any;
}

/* -------------------------------------
 * General Config APIs
 * ------------------------------------- */
export const setGeneralConfig = async (
  agentId: string,
  config: GeneralConfigCreate,
): Promise<GeneralConfig> => {
  const response = await apiClient.post(
    `/api/automations/config/${agentId}`,
    config,
  );
  return response.data;
};

export const getGeneralConfig = async (
  agentId: string,
): Promise<GeneralConfig | null> => {
  try {
    const response = await apiClient.get(`/api/automations/config/${agentId}`);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError && error.status === 404) {
      // A 404 error means the config doesn't exist yet
      return null;
    } else {
      console.error(error);
      throw error;
    }
  }
};

export const updateGeneralConfig = async (
  agentId: string,
  updates: GeneralConfigUpdate,
): Promise<GeneralConfig> => {
  const response = await apiClient.put(
    `/api/automations/config/${agentId}`,
    updates,
  );
  return response.data;
};

/* -------------------------------------
 * Automation APIs
 * ------------------------------------- */
export const createAutomation = async (
  agentId: string,
  data: AutomationCreate,
): Promise<Automation> => {
  const response = await apiClient.post(
    `/api/automations/${agentId}/automations`,
    data,
  );
  return response.data;
};

export const listAutomations = async (
  agentId: string,
  params?: {
    // If you want to filter by a certain type or property from the doc's metadata
    // you can pass it as a param, but there's no strict definition here.
    [key: string]: any;
  },
): Promise<Automation[]> => {
  const response = await apiClient.get(
    `/api/automations/${agentId}/automations`,
    {
      params,
    },
  );
  return response.data.automations;
};

export const getAutomation = async (
  agentId: string,
  automationId: string,
): Promise<Automation> => {
  const response = await apiClient.get(
    `/api/automations/${agentId}/automations/${automationId}`,
  );
  return response.data;
};

export const updateAutomation = async (
  agentId: string,
  automationId: string,
  updates: Record<string, any>,
): Promise<Automation> => {
  const response = await apiClient.put(
    `/api/automations/${agentId}/automations/${automationId}`,
    updates,
  );
  return response.data;
};

export const persistAutomationStatus = async (
  agentId: string,
  automationId: string,
  enabled: boolean,
): Promise<{ message: string }> => {
  const response = await apiClient.post(
    `/api/automations/${agentId}/automations/${automationId}/toggle`,
    { enabled },
  );
  return response.data;
};

/* -------------------------------------
 * Limits & Costs
 * ------------------------------------- */
export const calculateCosts = async (
  agentId: string,
  automationIds?: string[],
): Promise<AutomationCosts> => {
  const response = await apiClient.post(`/api/automations/${agentId}/costs`, {
    automation_ids: automationIds || [],
  });
  return response.data;
};

/* -------------------------------------
 * React Query Options
 * ------------------------------------- */
export const automationQueries = {
  generalConfig: (agentId: string) =>
    queryOptions({
      queryKey: ["automation-config", agentId],
      queryFn: () => getGeneralConfig(agentId),
      retry: 1,
    }),
  automations: (agentId: string, params?: Record<string, any>) =>
    queryOptions({
      queryKey: ["automations", agentId, params],
      queryFn: () => listAutomations(agentId, params),
    }),
  automation: (agentId: string, automationId: string) =>
    queryOptions({
      queryKey: ["automation", agentId, automationId],
      queryFn: () => getAutomation(agentId, automationId),
    }),
  costs: (agentId: string) =>
    queryOptions({
      queryKey: ["automation-costs", agentId],
      queryFn: async () => {
        try {
          return await calculateCosts(agentId);
        } catch (error) {
          console.error("Costs calculation error:", error);
          return null;
        }
      },
      enabled: Boolean(agentId),
    }),
};
